import {
  explicitMaybe,
  mergeObjectDecoders,
  numberToStringDecoder,
  stringLiteral,
} from '@execonline-inc/decoders';
import { paginationMetadataDecoder } from '@execonline-inc/resource';
import { structuredMaybeDecoder } from '@execonline-inc/structured-maybe';
import { alreadyTranslatedText } from '@execonline-inc/translations';
import { identity } from '@kofno/piper';
import {
  Decoder,
  array,
  boolean,
  date,
  dateISO,
  field,
  maybe,
  number,
  oneOf,
  string,
  succeed,
} from 'jsonous';
import { Result } from 'resulty';
import { combineDecoders } from '../../Decoders';
import { resourceDecoder, resourceWithMetadataDecoder } from '../../Resource/Decoders';
import { formTemplatesDecoder } from '../../Resource/FormTemplates/Decoders';
import {
  CompetencyFilterOptionData,
  LearningDesignState,
  ProductTypeFilterOptionData,
} from './LearningCollection/Types';
import {
  Attribute,
  AiRecommendation,
  AttributeResource,
  DynamicRecommendation,
  Experience,
  ExperienceResource,
  ExperienceResources,
  ExperienceType,
  ExperiencesResource,
  GroupCoachingCollection,
  LearningCollection,
  LearningCollectionBase,
  LearningCollectionResource,
  LearningCollectionSummariesResource,
  LearningCollectionSummary,
  LearningCollectionSummaryResource,
  LearningCollectionsMetadata,
  LearningDesign,
  LearningDesignResource,
  LearningDesignResourcesPayload,
  LearningDesignsResource,
  OfferingType,
  OnDemandProgramInstanceOption,
  OnDemandProgramInstanceOptionParts,
  ProductCollection,
  ProductCollectionBase,
  ProductCollectionDisplayType,
  ProductCollectionResource,
  ProductCollectionResources,
  ProductLicense,
  ProductLicenseResource,
  ProductLicenses,
  ProductLicensesResource,
  ProductType,
  ProgramFamilyShareUrl,
  ProgramFamilyShareUrlResource,
  ProgramInstanceOptionBase,
  ProgramInstanceOptions,
  ProtectedField,
  ProtectedFieldKind,
  PublicNameProgramFamilyShareUrl,
  RecommendationEngine,
  ScheduledProgramInstanceOption,
  ScheduledProgramInstanceOptionParts,
  SchoolPartner,
  SchoolPartnerResource,
  ShareUrl,
  ShareUrlResource,
  SortOptionValue,
  Status,
  SupportedLanguage,
  SupportedLanguageResource,
} from './Types';

export const experienceTypeDecoder: Decoder<ExperienceType> = oneOf([
  stringLiteral<ExperienceType>('program'),
  stringLiteral<ExperienceType>('coaching'),
  stringLiteral<ExperienceType>('group-coaching'),
  stringLiteral<ExperienceType>('program-sequence'),
]);

export const productTypeDecoder: Decoder<ProductType> = oneOf([
  stringLiteral<ProductType>('Core'),
  stringLiteral<ProductType>('Add-on'),
]);

const supportedLanguageDecoder: Decoder<SupportedLanguage> = succeed({})
  .assign('code', field('code', string))
  .assign('englishName', field('english_name', string))
  .assign('localName', field('local_name', string));

export const supportedLanguageResourceDecoder: Decoder<SupportedLanguageResource> =
  resourceDecoder(supportedLanguageDecoder);

export const attributeDecoder: Decoder<Attribute> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', explicitMaybe(alreadyTranslatedText)))
  .assign('description', field('description', explicitMaybe(alreadyTranslatedText)))
  .assign('key', field('key', explicitMaybe(string)))
  .assign('type', field('type', string));

export const attributeResourceDecoder: Decoder<AttributeResource> =
  resourceDecoder(attributeDecoder);

const programInstanceOptionBaseDecoder: Decoder<ProgramInstanceOptionBase> = succeed({})
  .assign('id', field('id', number))
  .assign('title', field('title', string))
  .assign('startOn', field('start_on', date))
  .assign('endOn', field('end_on', explicitMaybe(date)));

const onDemandProgramInstanceOptionPartsDecoder: Decoder<OnDemandProgramInstanceOptionParts> =
  succeed({}).assign('kind', field('kind', stringLiteral('on-demand')));

const scheduledProgramInstanceOptionPartsDecoder: Decoder<ScheduledProgramInstanceOptionParts> =
  succeed({}).assign('kind', field('kind', stringLiteral('scheduled')));

const onDemandProgramInstanceOptionDecoder: Decoder<OnDemandProgramInstanceOption> =
  mergeObjectDecoders(programInstanceOptionBaseDecoder, onDemandProgramInstanceOptionPartsDecoder);

const scheduledProgramInstanceOptionDecoder: Decoder<ScheduledProgramInstanceOption> =
  mergeObjectDecoders(programInstanceOptionBaseDecoder, scheduledProgramInstanceOptionPartsDecoder);

export const programInstanceOptionsDecoder: Decoder<ProgramInstanceOptions> = succeed({})
  .assign('onDemand', field('on_demand', explicitMaybe(onDemandProgramInstanceOptionDecoder)))
  .assign('scheduled', field('scheduled', array(scheduledProgramInstanceOptionDecoder)));

// Fall back to 'aep' for any offering_type the client doesn't model (e.g. the
// retiring 'msuite' type). Without this, a single unrecognized value rejects
// the entire learning-collection decode and leaves the page stuck loading.
// 'aep' is the correct bucket for msuite specifically: the backend's
// public_program_type maps both 'aep' and 'msuite' to "On-Demand".
export const offeringTypeDecoder: Decoder<OfferingType> = oneOf([
  stringLiteral<OfferingType>('epc'),
  stringLiteral<OfferingType>('aep'),
  stringLiteral<OfferingType>('coaching'),
  stringLiteral<OfferingType>('group-coaching'),
  stringLiteral<OfferingType>('program-sequence'),
]).orElse(() => succeed<OfferingType>('aep'));

export const statusTypeDecoder: Decoder<Status> = oneOf([
  stringLiteral<Status>('Unlicensed'),
  stringLiteral<Status>('Licensed'),
]);

const experienceDecoder: Decoder<Experience> = succeed({})
  .assign('id', field('id', number))
  .assign('kind', field('kind', experienceTypeDecoder))
  .assign('shortName', field('short_name', explicitMaybe(string)))
  .assign('descriptor', field('descriptor', explicitMaybe(string)))
  .assign('title', field('title', alreadyTranslatedText))
  .assign('description', field('description', alreadyTranslatedText))
  .assign('totalHours', field('total_hours', explicitMaybe(number)))
  .assign('hoursPerWeek', field('hours_per_week', explicitMaybe(number)))
  .assign('schoolPartnerLogoUrl', field('school_partner_logo_url', explicitMaybe(string)))
  .assign('schoolPartnerName', field('school_partner_name', string))
  .assign('featured', field('featured', explicitMaybe(dateISO)))
  .assign('publicDetailedDescriptionHtml', field('public_detailed_description_html', string))
  .assign('competencies', field('competencies', array(attributeResourceDecoder)))
  .assign(
    'availableOnDemandInstanceId',
    field('available_on_demand_instance_id', explicitMaybe(number)),
  )
  .assign(
    'supportedLanguages',
    field('supported_languages', array(supportedLanguageResourceDecoder)),
  )
  .assign(
    'programInstanceOptions',
    field('program_instance_options', programInstanceOptionsDecoder),
  )
  .assign('offeringType', field('offering_type', offeringTypeDecoder))
  .assign('status', field('status', statusTypeDecoder));

const experienceResourceDecoder: Decoder<ExperienceResource> = resourceDecoder(experienceDecoder);

export const experienceResourcesDecoder: Decoder<ExperienceResources> =
  array(experienceResourceDecoder);

export const experiencesResourceDecoder: Decoder<ExperiencesResource> = resourceDecoder(
  experienceResourcesDecoder,
);

const learningDesignStateDecoder: Decoder<LearningDesignState> = oneOf([
  stringLiteral<LearningDesignState>('curated'),
  stringLiteral<LearningDesignState>('curated-custom'),
  stringLiteral<LearningDesignState>('custom'),
  stringLiteral<LearningDesignState>('empty'),
  stringLiteral<LearningDesignState>('build-your-own'),
  stringLiteral<LearningDesignState>('browse-all'),
]);

export const sortOptionValueDecoder: Decoder<SortOptionValue> = oneOf([
  stringLiteral<SortOptionValue>('name-asc'),
  stringLiteral<SortOptionValue>('name-desc'),
  stringLiteral<SortOptionValue>('updated_at-asc'),
  stringLiteral<SortOptionValue>('updated_at-desc'),
]);

const productCollectionDisplayTypeDecoder: Decoder<ProductCollectionDisplayType> = oneOf([
  stringLiteral<ProductCollectionDisplayType>('carousel'),
  stringLiteral<ProductCollectionDisplayType>('list'),
  stringLiteral<ProductCollectionDisplayType>('grid'),
]);

export const schoolPartnerDecoder: Decoder<SchoolPartner> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', alreadyTranslatedText));

export const schoolPartnerResourceDecoder: Decoder<SchoolPartnerResource> =
  resourceDecoder(schoolPartnerDecoder);

const productCollectionBaseDecoder: Decoder<ProductCollectionBase> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', alreadyTranslatedText))
  .assign('description', field('description', string))
  .assign('shortName', field('short_name', string))
  .assign('displayType', field('display_type', productCollectionDisplayTypeDecoder))
  .assign('displayButtonLabel', field('display_button_label', explicitMaybe(string)))
  .assign('experiencesSchoolPartnerUrls', field('experiences_school_partner_urls', array(string)))
  .assign('experiences', field('experiences', experienceResourcesDecoder))
  .assign('learningDesignState', field('learning_design_state', learningDesignStateDecoder));

export const learningDesignResourceDecoder: Decoder<LearningDesignResource> = resourceDecoder(
  learningDesignDecoderFn(),
).assign('templates', ({ links }) => field('templates', formTemplatesDecoder(links)));

function learningDesignDecoderFn(): Decoder<LearningDesign> {
  return new Decoder(
    (u: unknown): Result<string, LearningDesign> =>
      productCollectionBaseDecoder
        .assign('kind', field('kind', stringLiteral<'learning-design'>('learning-design')))
        .assign('totalHours', field('total_hours', numberToStringDecoder))
        .assign('totalExperiences', field('total_experiences', numberToStringDecoder))
        .assign('totalSchoolPartners', field('total_school_partners', numberToStringDecoder))
        .assign('competencies', field('competencies', array(attributeResourceDecoder)))
        .assign('offeringTypes', field('offering_types', array(offeringTypeDecoder)))
        .assign('schoolPartners', field('school_partners', array(schoolPartnerResourceDecoder)))
        .assign(
          'sourceLearningDesign',
          field('source_learning_design', explicitMaybe(learningDesignResourceDecoder)),
        )
        .assign('browseAll', field('browse_all', boolean))
        .decodeAny(u)
        .mapError(() => `Expected a learning design, but got ${JSON.stringify(u)}`),
  );
}

const competencyFilterOptionDecoder: Decoder<CompetencyFilterOptionData> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('count', field('count', number));

const productTypeFilterOptionDecoder: Decoder<ProductTypeFilterOptionData> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', offeringTypeDecoder))
  .assign('count', field('count', number));

export const learningDesignResourcesDecoder: Decoder<LearningDesignResourcesPayload> = succeed({})
  .assign('timestamp', field('timestamp', string))
  .assign(
    'learningDesignResources',
    field('learning_design_resources', array(learningDesignResourceDecoder)),
  )
  .assign(
    'competencyFilterOptions',
    field('competency_filter_options', array(competencyFilterOptionDecoder)),
  )
  .assign(
    'productTypeFilterOptions',
    field('product_type_filter_options', array(productTypeFilterOptionDecoder)),
  );

export const learningDesignsResourceDecoder: Decoder<LearningDesignsResource> =
  resourceWithMetadataDecoder(learningDesignResourcesDecoder, paginationMetadataDecoder);

const recommendationEngineDecoder: Decoder<RecommendationEngine> = combineDecoders(
  productCollectionBaseDecoder,
  succeed({}).assign(
    'kind',
    field('kind', stringLiteral<'recommendation-engine'>('recommendation-engine')),
  ),
);

const groupCoachingCollectionDecoder: Decoder<GroupCoachingCollection> = combineDecoders(
  productCollectionBaseDecoder,
  succeed({}).assign('kind', field('kind', stringLiteral<'group-coaching'>('group-coaching'))),
);

const dynamicRecommendationDecoder: Decoder<DynamicRecommendation> =
  productCollectionBaseDecoder.assign(
    'kind',
    field('kind', stringLiteral('dynamic-recommendation')),
  );

const aiRecommendationDecoder: Decoder<AiRecommendation> = combineDecoders(
  productCollectionBaseDecoder,
  succeed({}).assign(
    'kind',
    field('kind', stringLiteral<'ai-recommendation'>('ai-recommendation')),
  ),
);

export const productCollectionResourceDecoder: Decoder<ProductCollectionResource> = resourceDecoder(
  oneOf([
    learningDesignDecoderFn().map<ProductCollection>(identity),
    groupCoachingCollectionDecoder.map<ProductCollection>(identity),
    recommendationEngineDecoder.map<ProductCollection>(identity),
    dynamicRecommendationDecoder.map<ProductCollection>(identity),
    aiRecommendationDecoder.map<ProductCollection>(identity),
  ]),
).assign('templates', ({ links }) => field('templates', formTemplatesDecoder(links)));

export const productCollectionResourcesDecoder: Decoder<ProductCollectionResources> = array(
  productCollectionResourceDecoder,
);

const shareUrlDecoder: Decoder<ShareUrl> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('useCaseId', field('use_case_id', number));

export const shareUrlResourceDecoder: Decoder<ShareUrlResource> = resourceDecoder(shareUrlDecoder);

const protectedFieldKindDecoder: Decoder<ProtectedFieldKind> = oneOf([
  stringLiteral<ProtectedFieldKind>('readonly'),
  stringLiteral<ProtectedFieldKind>('writable'),
]);

export const protectedFieldDecoder = <T>(decoder: Decoder<T>): Decoder<ProtectedField<T>> =>
  succeed({})
    .assign('kind', field('kind', protectedFieldKindDecoder))
    .assign('value', field('value', decoder));

const programFamilyShareUrlDecoder: Decoder<ProgramFamilyShareUrl> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', maybe(string)))
  .assign('useCaseId', field('use_case_id', maybe(number)))
  .assign('programFamilyId', field('program_family_id', explicitMaybe(number)))
  .assign('deliveryChannel', field('delivery_channel', string))
  .assign('active', field('active', boolean))
  .assign('user', field('user', string))
  .assign('createdAt', field('created_at', date))
  .assign(
    'programInstanceOptions',
    field('program_instance_options', programInstanceOptionsDecoder),
  );

export const programFamilyShareUrlResourceDecoder: Decoder<ProgramFamilyShareUrlResource> =
  resourceDecoder(programFamilyShareUrlDecoder);

const programFamilyShareUrlsDecoder: Decoder<PublicNameProgramFamilyShareUrl> = succeed({})
  .assign('name', field('name', alreadyTranslatedText))
  .assign('shareUrl', field('share_url', programFamilyShareUrlResourceDecoder))
  .assign('experience', field('experience', experienceResourceDecoder));

const learningCollectionBaseDecoder: Decoder<LearningCollectionBase> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('internalName', field('internal_name', structuredMaybeDecoder(string)))
  .assign('publishedAt', field('published_at', structuredMaybeDecoder(dateISO)))
  .assign('shareUrl', field('share_url', structuredMaybeDecoder(shareUrlResourceDecoder)))
  .assign('competencies', field('competencies', array(attributeResourceDecoder)));

const learningCollectionSummaryDecoder: Decoder<LearningCollectionSummary> = combineDecoders(
  learningCollectionBaseDecoder,
  succeed({})
    .assign('experiencesCount', field('experiences_count', number))
    .assign('studentMembersCount', field('student_members_count', number))
    .assign(
      'lastRegistrationDate',
      field('last_registration_date', structuredMaybeDecoder(dateISO)),
    ),
);

const learningCollectionDecoder: Decoder<LearningCollection> = combineDecoders(
  learningCollectionBaseDecoder,
  succeed({})
    .assign('description', field('description', string))
    .assign('productCollections', field('product_collections', productCollectionResourcesDecoder))
    .assign('competencies', field('competencies', array(attributeResourceDecoder)))
    .assign(
      'programFamilyShareUrls',
      field('learning_collection_program_family_share_urls', array(programFamilyShareUrlsDecoder)),
    )
    .assign('possibleShareUrlCount', field('possible_share_url_count', number))
    .assign('duplicateProgramDeepLinkCount', field('duplicate_program_deep_link_count', number))
    .assign('totalShareUrlCount', field('total_share_url_count', number))
    .assign('filteredShareUrlCount', field('filtered_share_url_count', number)),
);

export const learningCollectionSummaryResourceDecoder: Decoder<LearningCollectionSummaryResource> =
  resourceDecoder(learningCollectionSummaryDecoder);

export const learningCollectionResourceDecoder: Decoder<LearningCollectionResource> =
  resourceDecoder(learningCollectionDecoder);

const learningCollectionsMetadataDecoder: Decoder<LearningCollectionsMetadata> =
  paginationMetadataDecoder.assign('totalUnfilteredCount', field('total_unfiltered_count', number));

export const learningCollectionSummariesResourceDecoder: Decoder<LearningCollectionSummariesResource> =
  resourceWithMetadataDecoder(
    array(learningCollectionSummaryResourceDecoder),
    learningCollectionsMetadataDecoder,
  );

const productLicenseDecoder: Decoder<ProductLicense> = succeed({})
  .assign('id', field('id', number))
  .assign('title', field('title', alreadyTranslatedText))
  .assign('licenseQuantity', field('license_quantity', number))
  .assign('productType', field('product_type', productTypeDecoder))
  .assign('daysUntilRenewal', field('days_until_renewal', number))
  .assign(
    'licenseTypePublicName',
    field('license_type_public_name', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypePublicDescription',
    field('license_type_public_description', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypeWhoIsItFor',
    field('license_type_who_is_it_for', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypeIncludesFollowingContentHeader',
    field('license_type_includes_following_content_header', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypeIncludesFollowingContentDesc',
    field('license_type_includes_following_content_desc', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypeCoachingPfLabel',
    field('license_type_coaching_pf_label', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypeAepPfLabel',
    field('license_type_aep_pf_label', explicitMaybe(alreadyTranslatedText)),
  )
  .assign(
    'licenseTypeEpcPfLabel',
    field('license_type_epc_pf_label', explicitMaybe(alreadyTranslatedText)),
  )
  .assign('competencies', field('competencies', array(attributeResourceDecoder)))
  .assign('extensionIds', field('extension_ids', array(number)))
  .assign('activationsRemaining', field('activations_remaining', explicitMaybe(number)))
  .assign('offeringTypes', field('offering_types', array(offeringTypeDecoder)))
  .assign('displayActivationsRemaining', field('display_activations_remaining', boolean))
  .assign('displayRenewalNotice', field('display_renewal_notice', boolean))
  .assign('endOn', field('end_on', dateISO));

export const productLicenseResourceDecoder: Decoder<ProductLicenseResource> =
  resourceDecoder(productLicenseDecoder);

const productLicensesDecoder: Decoder<ProductLicenses> = succeed({})
  .assign(
    'activeProductLicenses',
    field('active_product_licenses', array(productLicenseResourceDecoder)),
  )
  .assign(
    'inactiveProductLicenses',
    field('inactive_product_licenses', array(productLicenseResourceDecoder)),
  );
export const productLicensesResourceDecoder: Decoder<ProductLicensesResource> =
  resourceDecoder(productLicensesDecoder);
